import { useMemo } from "react";
import type { BudgetLineRow, TimeEntry } from "@/hooks/use-project-detail";
import { parseTaskDescription } from "@/lib/format";

/** VIB-1201: én føring i entriesPerUser-mapet, merket med hvordan den ble
 * koblet til budsjettlinjen — string-matchede vises diskret merket i UI
 * (usikker kobling), FK-matchede er den robuste, forventede stien. */
export interface LoggedEntryRef {
  entry: TimeEntry;
  matchType: "fk" | "string";
}

export interface BudgetLineLoggedHours {
  /** budget_line_id → totale loggede timer */
  totals: Map<string, number>;
  /** budget_line_id → (user_id → loggede timer for denne brukeren) */
  perUser: Map<string, Map<string, number>>;
  /** budget_line_id → (assignment_id → loggede timer for denne tildelingen).
   *  Fylles kun fra time_entries med satt assignment_id. Legacy-entries
   *  uten assignment_id påvirker ikke dette mapet — bruk perUser som fallback. */
  perAssignment: Map<string, Map<string, number>>;
  /** VIB-1201: budget_line_id → (user_id → underliggende føringer), for
   * hover-tooltipen i budsjett-tabben. Samme løkke som totals/perUser —
   * ingen ekstra iterasjon over time_entries. */
  entriesPerUser: Map<string, Map<string, LoggedEntryRef[]>>;
}

/**
 * Beregner loggede timer per budget_line og per bruker innenfor hver linje
 * fra time_entries. Bruker FK når den finnes, og faller tilbake til
 * string-matching mot phase + task_description ellers (legacy data uten
 * budget_line_id).
 *
 * Eksportert som pure funksjon i tillegg til hook så vi kan unit-teste uten å
 * mounte React.
 */
export function computeBudgetLineLoggedHours(
  timeEntries: TimeEntry[],
  budgetLines: BudgetLineRow[],
): BudgetLineLoggedHours {
  const totals = new Map<string, number>();
  const perUser = new Map<string, Map<string, number>>();
  const perAssignment = new Map<string, Map<string, number>>();
  const entriesPerUser = new Map<string, Map<string, LoggedEntryRef[]>>();

  const phaseMatches = (tePhase: string, blPhase: string) =>
    tePhase !== "" &&
    blPhase !== "" &&
    (tePhase.includes(blPhase) || blPhase.includes(tePhase));

  const taskMatches = (teTask: string, blDesc: string) =>
    teTask !== "" &&
    blDesc !== "" &&
    (teTask.includes(blDesc) || blDesc.includes(teTask));

  const addHours = (
    blId: string,
    userId: string | null,
    assignmentId: string | null,
    secs: number,
    entry: TimeEntry,
    matchType: "fk" | "string",
  ) => {
    const hours = secs / 3600;
    totals.set(blId, (totals.get(blId) ?? 0) + hours);
    if (userId) {
      if (!perUser.has(blId)) perUser.set(blId, new Map());
      const um = perUser.get(blId)!;
      um.set(userId, (um.get(userId) ?? 0) + hours);

      // VIB-1201: samle entry-referansen i samme løkke — ingen ekstra pass.
      if (!entriesPerUser.has(blId)) entriesPerUser.set(blId, new Map());
      const em = entriesPerUser.get(blId)!;
      const list = em.get(userId) ?? [];
      list.push({ entry, matchType });
      em.set(userId, list);
    }
    if (assignmentId) {
      if (!perAssignment.has(blId)) perAssignment.set(blId, new Map());
      const am = perAssignment.get(blId)!;
      am.set(assignmentId, (am.get(assignmentId) ?? 0) + hours);
    }
  };

  for (const te of timeEntries) {
    const secs = te.duration_seconds ?? 0;
    if (secs === 0) continue;

    // 1. Direct FK match: budget_line_id is set on the time entry
    if (te.budget_line_id) {
      const directMatch = budgetLines.find((bl) => bl.id === te.budget_line_id);
      if (directMatch) {
        addHours(directMatch.id, te.user_id ?? null, te.assignment_id ?? null, secs, te, "fk");
        continue;
      }
    }

    // 2. String-matching fallback for entries without budget_line_id
    const { phase: tePhase, task: teTask } = parseTaskDescription(te.task_description);
    const tePhaseLower = tePhase.toLowerCase();
    const teTaskLower = teTask.toLowerCase();

    // 2a. Both phase and task match
    const exactMatch = budgetLines.find((bl) => {
      const blPhase = (bl.phase ?? "").toLowerCase();
      const blDesc = (bl.description ?? "").toLowerCase();
      return phaseMatches(tePhaseLower, blPhase) && taskMatches(teTaskLower, blDesc);
    });

    if (exactMatch) {
      addHours(exactMatch.id, te.user_id ?? null, null, secs, te, "string");
      continue;
    }

    // 2b. Task-only match (phase unknown)
    if (tePhaseLower === "" && teTaskLower !== "") {
      const taskOnlyMatch = budgetLines.find((bl) => {
        const blDesc = (bl.description ?? "").toLowerCase();
        return taskMatches(teTaskLower, blDesc);
      });
      if (taskOnlyMatch) {
        addHours(taskOnlyMatch.id, te.user_id ?? null, null, secs, te, "string");
        continue;
      }
    }

    // 2c. Phase-only: use assignments to find the right budget line for this user
    if (tePhaseLower !== "" && te.user_id) {
      const phaseLines = budgetLines.filter((bl) => {
        const blPhase = (bl.phase ?? "").toLowerCase();
        return phaseMatches(tePhaseLower, blPhase) && bl.cost_type === "internal";
      });
      const userAssignedLine = phaseLines
        .filter((bl) => bl.assignments.some((a) => a.user_id === te.user_id))
        .sort((a, b) => {
          const aHours = a.assignments.find((x) => x.user_id === te.user_id)?.hours ?? 0;
          const bHours = b.assignments.find((x) => x.user_id === te.user_id)?.hours ?? 0;
          return bHours - aHours;
        })[0];
      if (userAssignedLine) {
        addHours(userAssignedLine.id, te.user_id, null, secs, te, "string");
        continue;
      }
      if (phaseLines.length > 0) {
        addHours(phaseLines[0].id, te.user_id, null, secs, te, "string");
        continue;
      }
    }

    // 2d. Last resort: any phase match
    const phaseBl = budgetLines.find((bl) => {
      const blPhase = (bl.phase ?? "").toLowerCase();
      return phaseMatches(tePhaseLower, blPhase);
    });
    if (phaseBl) {
      addHours(phaseBl.id, te.user_id ?? null, null, secs, te, "string");
    }
  }

  // VIB-1201: sorter kronologisk innenfor hver bruker/linje (flere økter
  // samme dag som separate rader, kronologisk — suksesskriterium).
  for (const userMap of entriesPerUser.values()) {
    for (const list of userMap.values()) {
      list.sort((a, b) => (a.entry.start_date ?? "").localeCompare(b.entry.start_date ?? ""));
    }
  }

  return { totals, perUser, perAssignment, entriesPerUser };
}

/**
 * useMemo-wrapper rundt `computeBudgetLineLoggedHours` for bruk i komponenter.
 * Memoiserer på time_entries og budget_lines.
 */
export function useBudgetLineLoggedHours(
  timeEntries: TimeEntry[],
  budgetLines: BudgetLineRow[],
): BudgetLineLoggedHours {
  return useMemo(
    () => computeBudgetLineLoggedHours(timeEntries, budgetLines),
    [timeEntries, budgetLines],
  );
}
